import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { ListingType } from 'algolia-api/types/ListingType';
import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import { LegacyTertiaryButton } from 'components/Button/LegacyTertiaryButton';
import { PrimaryButton } from 'components/Button/PrimaryButton';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { useSaveListing } from 'hooks/useSaveListing';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypePluralHumanNameMap';
import { OrgType } from 'utils/constants/general/orgTypes';

type Props = {
  disabled?: boolean;
  listingId: string;
  listingName: string;
  listingType: ListingType;
  orgId?: string;
  orgName?: string;
  orgType?: OrgType;
};

const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

export function SaveButton({
  disabled,
  listingId,
  listingName,
  listingType,
  orgId,
  orgName,
  orgType,
}: Props) {
  const {
    saved,
    saveOrg,
    setShowOrgModal,
    showOrgModal,
    canSave,
    saveListing,
    unsaveListing,
  } = useSaveListing({
    listingId,
    listingType,
    listingName,
    orgId,
    orgName,
    orgType,
  });

  if (!canSave) return null;

  const buttonText = saved ? getText('Unsave') : getText('Save');

  const onConfirm = async () => {
    await saveOrg();
    setShowOrgModal(false);
  };

  const onDecline = async () => {
    setShowOrgModal(false);
  };

  return (
    <Tooltip
      content={
        <div data-qa-id="save-listing-tooltip">
          <Box mb={3}>
            {sprintf(
              getText(
                'Would you like to get alerts when this organization posts new %(listingTypeName)s?',
              ),
              {
                listingTypeName:
                  LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[listingType],
              },
            )}
          </Box>

          <ButtonContainer>
            <LegacyTertiaryButton
              onClick={onDecline}
              data-qa-id="decline-button"
            >
              {getText('No, thank you')}
            </LegacyTertiaryButton>
            <PrimaryButton
              onClick={onConfirm}
              disabled={false}
              data-qa-id="confirm-button"
            >
              {getText('Yes!')}
            </PrimaryButton>
          </ButtonContainer>
        </div>
      }
      placement="bottom-left"
      closeable
      size="large"
      variant="brand-blue"
      triggerCondition={showOrgModal}
      withOverlay
    >
      <div>
        <Button
          disabled={disabled}
          aria-label={buttonText}
          variant="secondary"
          icon={{ type: 'normalized', name: 'heart' }}
          data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
          onClick={saved ? unsaveListing : saveListing}
          title={buttonText}
        >
          {buttonText}
        </Button>
      </div>
    </Tooltip>
  );
}
